import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, ApiUnreachableError, type ApiClient } from "../api/client.js";
import type { NewPlanDimension, PlanDimensionPatch, PlanDimensionRecord } from "../api/types.js";
import {
  add as addLocal,
  dimensionOf,
  isMemoryOnly,
  kindOf,
  list as listLocal,
  newDimensionId,
  pointsOf,
  remove as removeLocal,
  setLabel as setLocalLabel,
  subscribe as subscribeLocal,
  type PlanDimension,
  type PlanDimensionKind,
  type PlanDimensionPoint,
} from "./plan-annotations.js";

/**
 * The plan's hand-drawn dimensions — lengths, angles and areas — saved with
 * the survey and shared with the team (migration 0039).
 *
 * Before 2026-09-11 they lived in one browser's localStorage only
 * (`plan-annotations.ts`). On first load against the API, any found there for
 * this survey are MOVED: each is sent under its browser id as `externalRef`
 * (so a retried move is a read on the server, never a second copy) and deleted
 * from this browser once the survey holds it. One that cannot be moved stays
 * here, is still drawn, and is tried again next time; the panel says how many.
 *
 * Without an API (a plan drawn with no `dimensions` source, or an API older
 * than the route) the plan keeps the browser-only behaviour it always had,
 * and says so.
 *
 * Optimistic with rollback, as the plan's objects are: a new dimension is on
 * the plan at once, and one the server refuses is taken back off with the
 * reason in words.
 */

export interface PlanDimensionsSource {
  /** `null`: this API has no dimensions route. */
  list(): Promise<readonly PlanDimensionRecord[] | null>;
  create(input: NewPlanDimension): Promise<PlanDimensionRecord>;
  update(id: string, patch: PlanDimensionPatch): Promise<PlanDimensionRecord>;
  remove(id: string): Promise<void>;
}

export function planDimensionsFromApi(client: ApiClient, surveyId: string): PlanDimensionsSource {
  return {
    list: () => client.planDimensions(surveyId),
    create: (input) => client.createPlanDimension(surveyId, input),
    update: (id, patch) => client.updatePlanDimension(surveyId, id, patch),
    remove: (id) => client.deletePlanDimension(surveyId, id),
  };
}

/** Where the dimensions on screen are kept — what the panel's note says. */
export type DimensionsHome = "server" | "browser" | "memory" | "loading";

export interface PlanDimensionsState {
  readonly home: DimensionsHome;
  readonly dimensions: readonly PlanDimension[];
  /** Moved from this browser to the survey on this load. */
  readonly moved: number;
  /** Still only in this browser: the move failed for these, and will be retried. */
  readonly stranded: number;
  readonly error: string | null;
  readonly add: (story: number, kind: PlanDimensionKind, points: readonly PlanDimensionPoint[]) => string;
  readonly remove: (id: string) => void;
  readonly setLabel: (id: string, label: string) => void;
  readonly dismissError: () => void;
}

function reasonOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof ApiUnreachableError) return error.message;
  return "Something went wrong saving it.";
}

function fromRecord(record: PlanDimensionRecord): PlanDimension {
  return dimensionOf(record.id, record.storey, record.kind, record.points, record.label);
}

function toInput(dimension: PlanDimension, externalRef?: string): NewPlanDimension {
  return {
    storey: dimension.story,
    kind: kindOf(dimension),
    points: pointsOf(dimension),
    ...(dimension.label ? { label: dimension.label } : {}),
    ...(externalRef ? { externalRef } : {}),
  };
}

const PROVISIONAL = "pending-dim-";

export function usePlanDimensions(source: PlanDimensionsSource | undefined, surveyId: string): PlanDimensionsState {
  const [mode, setMode] = useState<"server" | "local" | "loading">(source ? "loading" : "local");
  const [server, setServer] = useState<readonly PlanDimension[]>([]);
  const [local, setLocal] = useState<readonly PlanDimension[]>(() => (source ? [] : listLocal(surveyId)));
  const [memoryOnly, setMemoryOnly] = useState(isMemoryOnly);
  const [moved, setMoved] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const counter = useRef(0);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Browser-only: follow other tabs of this browser, as before.
  useEffect(() => {
    if (mode !== "local") return;
    setLocal(listLocal(surveyId));
    setMemoryOnly(isMemoryOnly());
    return subscribeLocal(surveyId, () => setLocal(listLocal(surveyId)));
  }, [mode, surveyId]);

  useEffect(() => {
    if (!source) {
      setMode("local");
      return;
    }
    let current = true;
    setMode("loading");
    void (async () => {
      let first: readonly PlanDimensionRecord[] | null;
      try {
        first = await source.list();
      } catch (err) {
        if (!current) return;
        // The survey's dimensions cannot be read: draw this browser's, and say why.
        setError(`The survey's dimensions could not be loaded. ${reasonOf(err)}`);
        setLocal(listLocal(surveyId));
        setMode("local");
        return;
      }
      if (!current) return;
      if (first === null) {
        // In the same render as the switch, so the plan never shows an empty list first.
        setLocal(listLocal(surveyId));
        setMode("local");
        return;
      }
      // Move what this browser still holds, then read the survey's again.
      const held = listLocal(surveyId);
      let movedNow = 0;
      for (const dimension of held) {
        try {
          await source.create(toInput(dimension, dimension.id));
          removeLocal(surveyId, dimension.id);
          movedNow += 1;
        } catch {
          // Kept here and tried again next time; counted as stranded below.
        }
      }
      let records = first;
      if (movedNow > 0) {
        try {
          records = (await source.list()) ?? first;
        } catch {
          // The first read stands; the moved ones will show on the next load.
        }
      }
      if (!current) return;
      setServer(records.map(fromRecord));
      setLocal(listLocal(surveyId));
      setMoved(movedNow);
      setMode("server");
    })();
    return () => {
      current = false;
    };
  }, [source, surveyId]);

  const isLocal = mode !== "server";
  const strandedIds = new Set(isLocal ? [] : local.map((d) => d.id));

  const add = useCallback<PlanDimensionsState["add"]>(
    (story, kind, points) => {
      if (mode !== "server" || !source) {
        const id = newDimensionId();
        setLocal(addLocal(surveyId, dimensionOf(id, story, kind, points)));
        setMemoryOnly(isMemoryOnly());
        return id;
      }
      counter.current += 1;
      const provisional = `${PROVISIONAL}${counter.current}`;
      const dimension = dimensionOf(provisional, story, kind, points);
      setServer((list) => [...list, dimension]);
      setError(null);
      source.create(toInput(dimension)).then(
        (record) => {
          if (alive.current) setServer((list) => list.map((d) => (d.id === provisional ? fromRecord(record) : d)));
        },
        (err: unknown) => {
          if (!alive.current) return;
          setServer((list) => list.filter((d) => d.id !== provisional));
          setError(`The dimension could not be saved, so it has been taken off the plan. ${reasonOf(err)}`);
        },
      );
      return provisional;
    },
    [mode, source, surveyId],
  );

  const remove = useCallback<PlanDimensionsState["remove"]>(
    (id) => {
      if (mode !== "server" || !source || local.some((d) => d.id === id)) {
        setLocal(removeLocal(surveyId, id));
        setMemoryOnly(isMemoryOnly());
        return;
      }
      if (id.startsWith(PROVISIONAL)) return;
      let removed: { dimension: PlanDimension; index: number } | undefined;
      setServer((list) => {
        const index = list.findIndex((d) => d.id === id);
        if (index < 0) return list;
        removed = { dimension: list[index] as PlanDimension, index };
        return list.filter((d) => d.id !== id);
      });
      setError(null);
      source.remove(id).then(
        () => undefined,
        (err: unknown) => {
          if (!alive.current) return;
          const back = removed;
          if (back) {
            setServer((list) => {
              if (list.some((d) => d.id === back.dimension.id)) return list;
              const next = [...list];
              next.splice(Math.min(back.index, next.length), 0, back.dimension);
              return next;
            });
          }
          setError(`The dimension could not be removed, so it has been put back. ${reasonOf(err)}`);
        },
      );
    },
    [mode, source, surveyId, local],
  );

  const setLabel = useCallback<PlanDimensionsState["setLabel"]>(
    (id, label) => {
      if (mode !== "server" || !source || local.some((d) => d.id === id)) {
        setLocal(setLocalLabel(surveyId, id, label));
        setMemoryOnly(isMemoryOnly());
        return;
      }
      if (id.startsWith(PROVISIONAL)) return;
      const trimmed = label.trim();
      let before: PlanDimension | undefined;
      setServer((list) =>
        list.map((d) => {
          if (d.id !== id) return d;
          before = d;
          const { label: _old, ...rest } = d;
          return trimmed === "" ? rest : { ...rest, label: trimmed };
        }),
      );
      setError(null);
      source.update(id, { label: trimmed === "" ? null : trimmed }).then(
        (record) => {
          if (alive.current) setServer((list) => list.map((d) => (d.id === id ? fromRecord(record) : d)));
        },
        (err: unknown) => {
          if (!alive.current) return;
          const undo = before;
          if (undo) setServer((list) => list.map((d) => (d.id === id ? undo : d)));
          setError(`The label could not be saved, so it has been put back. ${reasonOf(err)}`);
        },
      );
    },
    [mode, source, surveyId, local],
  );

  const dismissError = useCallback(() => setError(null), []);

  const home: DimensionsHome =
    mode === "loading" ? "loading" : mode === "server" ? "server" : memoryOnly ? "memory" : "browser";
  const dimensions = mode === "server" ? [...server, ...local] : local;

  return {
    home,
    dimensions,
    moved,
    stranded: strandedIds.size,
    error,
    add,
    remove,
    setLabel,
    dismissError,
  };
}
